import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { LogOut } from 'lucide-react';
import logo from '../logo.png';
import miniLogo from '../logo-mini.png';
import './Sidebar.css';
import useAuth from '../pages/useAuth';
import { superAdminNav } from './rolewiseNavbar/superAdminNav';
import { reportingManagerNav } from './rolewiseNavbar/reportingManagerNav';
import { userNav } from './rolewiseNavbar/userNav';
import { employeeNav } from './rolewiseNavbar/employeeNav';
import { hrNav } from './rolewiseNavbar/hrNav';
import { hrNavInternal } from './rolewiseNavbar/hrNavInternal';
import { hrNavRadiology } from './rolewiseNavbar/hrNavRadiology';
import { userNavInternal } from './rolewiseNavbar/userNavInternal';
import { userNavRadiology } from './rolewiseNavbar/userNavRadiology';
import HodDoctorsNav from './rolewiseNavbar/hodDoctorsNav';
import HodContractualNav from './rolewiseNavbar/hodContractualNav';
import HodPoojariNav from './rolewiseNavbar/hodPoojariNav';
import BranchManagerNav from './rolewiseNavbar/branchManagerNav';

const Sidebar = ({ isExpanded, mobileOpen, setMobileOpen, onToggle }) => {
     useAuth();
     const [isHovered, setIsHovered] = useState(false);
     const [adminOpen, setAdminOpen] = useState(false);
     const navigate = useNavigate();
     const [settingsOpen, setSettingsOpen] = useState(false);
     const [mastersOpen, setMastersOpen] = useState(false);
     const [isMobile, setIsMobile] = useState(window.innerWidth < 600);
     const [radiologyOpen, setRadiologyOpen] = useState(false);
     const [attendanceSummeryOpen, setAttendanceSummaryOpen] = useState(false); // ✅ Add this

     const shouldShowExpanded = isMobile ? mobileOpen : (isExpanded || isHovered);

     useEffect(() => {
          const handleResize = () => setIsMobile(window.innerWidth < 600);
          window.addEventListener('resize', handleResize);
          return () => window.removeEventListener('resize', handleResize);
     }, []);

     useEffect(() => {
          if (!isMobile || !mobileOpen) return;
          const handleClick = (e) => {
               if (!e.target.closest('.sidebar')) setMobileOpen(false);
          };
          document.addEventListener('mousedown', handleClick);
          return () => document.removeEventListener('mousedown', handleClick);
     }, [isMobile, mobileOpen, setMobileOpen]);

     const handleLogout = () => {
          sessionStorage.removeItem("token");
          sessionStorage.clear();
          localStorage.clear();
          navigate('/login');
     };

     const handleLogoClick = () => {
          navigate('/dashboard');
     };

     const user_role = sessionStorage.getItem('user_role');

     const navStyles = {
          navItem: {
               display: 'flex',
               alignItems: 'center',
               padding: '8px 12px',
               color: '#111',
               textDecoration: 'none',
               gap: '8px',
               borderRadius: 6,
               marginBottom: '4px',
          },
          navItemActive: {
               backgroundColor: '#e6f0ff',
               color: '#0b63ff',
          },
          submenu: {
               position: 'absolute',
               left: '100%',
               top: 0,
               background: '#fff',
               boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
               borderRadius: '8px',
               padding: '8px 0',
               minWidth: '200px',
               zIndex: 100,
               display: 'flex',
               flexDirection: 'column',
               alignItems: 'flex-start',
               gap: '4px',
          },
          submenuItem: {
               padding: '10px 18px',
               width: '100%',
               display: 'flex',
               alignItems: 'center',
               color: '#333',
               textDecoration: 'none',
               fontSize: '15px',
               border: 'none',
               background: 'none',
               cursor: 'pointer',
               borderBottom: '1px solid #f0f0f0',
          },
          submenuItemActive: {
               background: '#e6f7ff',
               color: '#1890ff',
          },
     };

     const getClassName = () => {
          if (isMobile) {
               return `sidebar${mobileOpen ? ' mobile-open' : ''}`;
          } else {
               return `sidebar ${shouldShowExpanded ? 'expanded' : 'collapsed'}`;
          }
     };

     return (
          <div
               className={getClassName()}
               onMouseEnter={() => !isMobile && setIsHovered(true)}
               onMouseLeave={() => !isMobile && setIsHovered(false)}
               style={{
                    overflowY: 'auto',
               }}
          >
               <div className="sidebar-logo-wrapper">
                    <button className="logo-button" onClick={handleLogoClick}>
                         <img
                              src={shouldShowExpanded ? logo : miniLogo}
                              alt="HRMS Logo"
                              className={`sidebar-logo ${shouldShowExpanded ? 'logo-expanded' : 'logo-collapsed'}`}
                         />
                    </button>
               </div>

               <nav className="sidebar-nav">
                    {user_role === 'SUPER_ADMIN' &&
                         superAdminNav(
                              shouldShowExpanded,
                              navStyles,
                              adminOpen,
                              setAdminOpen,
                              attendanceSummeryOpen,      // ✅ Pass this
                              setAttendanceSummaryOpen,   // ✅ Pass this
                              settingsOpen,
                              setSettingsOpen,
                              mastersOpen,
                              setMastersOpen,
                              radiologyOpen,
                              setRadiologyOpen
                         )
                    }
                    {user_role === 'REPORTING_MANAGER' && reportingManagerNav(shouldShowExpanded, navStyles, onToggle)}
                    {user_role === 'USER' && userNav(shouldShowExpanded, navStyles, onToggle)}
                    {user_role === 'EMPLOYEE' && employeeNav(shouldShowExpanded, navStyles, onToggle)}
                    {user_role === 'HR' && hrNav(shouldShowExpanded, navStyles, onToggle)}
                    {user_role === 'USER_RADIOLOGY' && userNavRadiology(shouldShowExpanded, navStyles, onToggle)}
                    {user_role === 'USER_INTERNAL' && userNavInternal(shouldShowExpanded, navStyles, onToggle)}
                    {user_role === 'HR_RADIOLOGY' && hrNavRadiology(shouldShowExpanded, navStyles, onToggle)}
                    {user_role === 'HR_INTERNAL' && hrNavInternal(shouldShowExpanded, navStyles, onToggle)}
                    {user_role === 'HOD_DOCTORS' && (
                         <HodDoctorsNav isHovered={shouldShowExpanded} styles={navStyles} onToggle={onToggle} />
                    )}
                    {user_role === 'HOD_CONTRACTUAL' && (
                         <HodContractualNav isHovered={shouldShowExpanded} styles={navStyles} onToggle={onToggle} />
                    )}
                    {user_role === 'HOD_POOJARI' && (
                         <HodPoojariNav isHovered={shouldShowExpanded} styles={navStyles} onToggle={onToggle} />
                    )}
                    {user_role === 'BRANCH_MANAGER' && (
                         <BranchManagerNav isHovered={shouldShowExpanded} styles={navStyles} onToggle={onToggle} />
                    )}
               </nav>

               <div className="sidebar-logout">
                    <button className="nav-item logout-btn" onClick={handleLogout}>
                         <LogOut size={20} />
                         {shouldShowExpanded && <span>Logout</span>}
                    </button>
               </div>
          </div>
     );
};

export default Sidebar;